import {
  DateRangeSetting,
  PatientState,
  PatientStatus,
  SexType,
} from '@lmt/shared-types';
import i18n from 'i18next';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form/dist/types';
import {
  add,
  min,
  max,
  startOfToday,
  parseISO,
  endOfDay,
  startOfDay,
} from 'date-fns';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';

export type PatientListConfigurationData = {
  name: string;
  isShared: boolean;
} & PatientListFiltersData;

export type PatientListFiltersData = {
  isFilterDisplayed?: boolean | null;
  hasFilterBySubtenants?: boolean | null;
  hasFilterByTags?: boolean | null;
  hasFilterBySexTypes?: boolean | null;
  hasFilterByStatuses?: boolean | null;
  hasFilterByStates?: boolean | null;
  hasFilterByLastActivity?: boolean | null;
  hasFilterByLastVisit?: boolean | null;
  hasFilterBySqlFilter?: boolean | null;
  subtenants?: number[] | null;
  tags?: number[] | null;
  sexTypes?: SexType[] | null;
  statuses?: PatientStatus[] | null;
  states?: PatientState[] | null;
  lastActivity?: DateRangeSetting | null;
  lastVisit?: DateRangeSetting | null;
  sqlFilter?: string | null;
};

export interface PatientListFilterComponentProps {
  values: PatientListFiltersData;
  onChange: (value: Partial<PatientListFiltersData>) => void;
  hasUserChanges?: boolean;
}

export function dateRangeSettingsToQueryParams(props: {
  lastActivity?: DateRangeSetting;
  lastVisit?: DateRangeSetting;
}) {
  const { lastActivity, lastVisit } = props;

  const lastActivityRange = dateRangeSettingToRange(
    'lastActivityRange',
    lastActivity,
  );

  const lastVisitRange = dateRangeSettingToRange('lastVisitRange', lastVisit);

  return { ...lastActivityRange, ...lastVisitRange };
}

export function dateRangeSettingToRange(
  paramName: string,
  setting?: DateRangeSetting,
) {
  if (
    !setting ||
    (!setting.isRelativeToCurrentMoment && !setting.absoluteMoment)
  )
    return null;

  const relativeTo = setting.isRelativeToCurrentMoment
    ? startOfToday()
    : setting.absoluteMoment!;
  const pointA = relativeTo;
  const pointB = add(relativeTo, {
    seconds: setting.rangeInSeconds ?? 0, // if rangeInSeconds is null then pointA and pointB will point to the same day
  });

  const result: Record<string, Date> = {};

  result[paramName + '_StartDate'] = startOfDay(min([pointA, pointB]));
  result[paramName + '_EndDate'] = endOfDay(max([pointA, pointB]));

  return result;
}

export interface PatientListConfigurationFormComponentProps {
  formMethods: UseFormReturn<PatientListConfigurationData>;
  // true if the form was opened with some preconfigured values (i.e. using 'Save as new').
  // in this case we shouldn't reset the filters to default
  hasPreconfiguredValues: boolean;
}

export const defaultPatientListConfigurationData: PatientListConfigurationData =
  {
    name: '',
    isShared: false,
    isFilterDisplayed: true,

    hasFilterBySubtenants: true,
    hasFilterByTags: true,
    hasFilterBySexTypes: true,
    hasFilterByStatuses: true,
    hasFilterByStates: true,
    hasFilterByLastActivity: false,
    hasFilterByLastVisit: false,
    hasFilterBySqlFilter: true,

    subtenants: [],
    tags: [],
    sexTypes: [],
    statuses: [],
    states: [],
    lastActivity: undefined,
    lastVisit: undefined,
    sqlFilter: '',
  };

export type EnumValue<T> = {
  value: T;
  label: string;
};

/*
 * e.g. getEnumNameByValue(SexType, SexType.Male) => 'Male'
 */
export function getEnumNameByValue(
  inputEnum: Record<string, number | string>,
  value: string | number | undefined,
): string | number | null {
  if (value === null || value === undefined) return null;
  for (const key of Object.keys(inputEnum)) {
    if (typeof key === 'string') if (inputEnum[key] === value) return key;
  }
  return null;
}

export function getEnumValuesDict(
  inputEnum: Record<string, number | string>,
  translationKey: string,
): EnumValue<number>[] {
  return Object.values(inputEnum)
    .filter((key) => !Number.isNaN(Number(key)))
    .map((key) => ({
      value: Number(key),
      label: i18n.t((translationKey + '.' + Number(key)) as any),
    }));
}

export function getDropDownValuesDict(
  strings: readonly string[],
  translationKey: string,
): EnumValue<string>[] {
  return strings.map((key) => ({
    value: key,
    label: i18n.t((translationKey + '.' + key) as any),
  }));
}

export const useSexTypeOptions = () =>
  useMemo(
    () =>
      getEnumValuesDict(SexType, 'sex_types').filter(
        (x) => x.value !== SexType.Diverse,
      ),
    [],
  );

export const usePatientStatesOptions = () =>
  useMemo(() => getEnumValuesDict(PatientState, 'patient_states'), []);

export const useFilteredPatientStatesOptions = () => {
  const claims = useClaims();
  let options = usePatientStatesOptions();
  return useMemo(() => {
    if (!claims.permissions.patientDeleteAndRestore) {
      options = options.filter((x) => x.value !== PatientState.Deleted);
    }
    if (!claims.permissions.patientAnonymizeAndDeletePermanently) {
      options = options.filter((x) => x.value !== PatientState.Anonymous);
    }
    return options;
  }, [options, claims]);
};

export const usePatientStatusesOptions = () =>
  useMemo(() => getEnumValuesDict(PatientStatus, 'patient_statuses'), []);
